import { useEffect, useState } from "react";
import axios from "axios";
import Seo from "../components/Seo";
import { Phone, MapPin, Clock, CheckCircle2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecoveryRequest = () => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    location: "",
    vehicle_reg: "",
    vehicle_make_model: "",
    breakdown_type: "",
    description: "",
    preferred_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ref, setRef] = useState(null);

  useEffect(() => {
    axios.get(`${API}/recovery/types`).then((r) => setTypes(r.data.types || [])).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.phone || !form.location || !form.breakdown_type) {
      setError("Please complete your name, phone, location and breakdown type.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.vehicle_reg) delete payload.vehicle_reg;
      if (!payload.vehicle_make_model) delete payload.vehicle_make_model;
      if (!payload.description) delete payload.description;
      if (!payload.preferred_time) delete payload.preferred_time;
      const { data } = await axios.post(`${API}/recovery/request`, payload);
      setRef(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send request. Please call 07979 210250.");
    } finally {
      setSubmitting(false);
    }
  };

  if (ref) {
    return (
      <div className="vd-page" data-testid="recovery-success">
        <Seo title="Recovery Requested" description="Your ValueDrive recovery callout has been received. Our team will contact you shortly." path="/recovery" />
        <section className="vd-container py-20 text-center">
          <div className="inline-flex items-center justify-center rounded-full mb-6" style={{ width: 72, height: 72, background: "rgba(0,180,80,0.12)" }}>
            <CheckCircle2 size={40} color="#00A35C" />
          </div>
          <h1 className="mb-4">Recovery request received</h1>
          <p className="vd-muted mb-2">Reference <span className="font-mono font-bold vd-heading">#{ref.id.slice(0, 8)}</span></p>
          <p className="vd-body max-w-lg mx-auto mb-8">Our team is on it. We&apos;ll call you back within minutes on the number you provided. If it&apos;s urgent, please also ring us directly.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:07979210250" className="vd-btn-primary" data-testid="recovery-success-call"><Phone size={16} /> Call 07979 210250</a>
            <a href="https://wa.me/447979210250" target="_blank" rel="noopener noreferrer" className="vd-btn-secondary" data-testid="recovery-success-whatsapp"><MessageCircle size={16} /> WhatsApp us</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="vd-page" data-testid="page-recovery">
      <Seo title="Request Vehicle Recovery" description="24/7 vehicle recovery across the UK — request a callout now and our team will be in touch within minutes." path="/recovery" />

      <section className="vd-section-secondary py-14 relative overflow-hidden">
        <div className="vd-red-accent-line" style={{ top: "20%" }} aria-hidden="true" />
        <div className="vd-red-accent-line" style={{ bottom: "20%" }} aria-hidden="true" />
        <div className="vd-container relative text-center">
          <h1 className="mb-3">Request Vehicle Recovery</h1>
          <p className="vd-muted max-w-2xl mx-auto">
            Tell us what&apos;s happened and where you are — our recovery team will call you back within minutes, 24/7 across the UK.
          </p>
        </div>
      </section>

      <section className="vd-container py-12 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <form onSubmit={submit} className="vd-card p-6" data-testid="recovery-form">
          <h3 className="mb-5">Callout details</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full Name *"><input className="vd-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="rec-full-name" placeholder="Your name" /></Field>
            <Field label="Phone Number *"><input className="vd-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="rec-phone" placeholder="07…" /></Field>
            <Field label="Email (optional)"><input type="email" className="vd-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="rec-email" placeholder="you@example.com" /></Field>
            <Field label="Preferred Time"><input className="vd-input" value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)} data-testid="rec-preferred-time" placeholder="ASAP / within 1 hour / today 6pm…" /></Field>
            <Field label="Location / Postcode *" full>
              <input className="vd-input" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="rec-location" placeholder="e.g. TS1 5AB, junction 34 A19, ASDA car park Middlesbrough" />
            </Field>
            <Field label="Vehicle Reg"><input className="vd-input" value={form.vehicle_reg} onChange={(e) => update("vehicle_reg", e.target.value.toUpperCase())} data-testid="rec-vehicle-reg" placeholder="AB12 XYZ" /></Field>
            <Field label="Vehicle Make / Model"><input className="vd-input" value={form.vehicle_make_model} onChange={(e) => update("vehicle_make_model", e.target.value)} data-testid="rec-vehicle-model" placeholder="Ford Focus 2019" /></Field>
            <Field label="Breakdown Type *" full>
              <select className="vd-select" value={form.breakdown_type} onChange={(e) => update("breakdown_type", e.target.value)} data-testid="rec-breakdown-type">
                <option value="">Select breakdown type</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="What's happened?" full>
              <textarea className="vd-input" rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="rec-description" placeholder="Any extra info that helps our driver reach you quickly" />
            </Field>
          </div>

          {error && <p className="mt-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="rec-error">{error}</p>}

          <button type="submit" disabled={submitting} className="vd-btn-primary w-full mt-6" data-testid="rec-submit">
            {submitting ? "Sending…" : "Request Recovery"}
          </button>
          <p className="vd-muted text-xs mt-3 text-center">By submitting you agree to be contacted by ValueDrive about this callout.</p>
        </form>

        <aside className="space-y-4">
          <div className="vd-card p-5" data-testid="recovery-aside">
            <p className="vd-heading font-bold mb-3">Need us immediately?</p>
            <a href="tel:07979210250" className="vd-btn-primary w-full mb-2" data-testid="rec-aside-call"><Phone size={16} /> Call 07979 210250</a>
            <a href="https://wa.me/447979210250" target="_blank" rel="noopener noreferrer" className="vd-btn-secondary w-full" data-testid="rec-aside-whatsapp"><MessageCircle size={16} /> WhatsApp us</a>
          </div>
          <div className="vd-card p-5">
            <p className="vd-heading font-bold mb-3">Recovery coverage</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Clock size={16} className="mt-0.5" color="var(--primary-red)" /> 24/7 callouts, every day of the year</li>
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5" color="var(--primary-red)" /> UK-wide, Middlesbrough & Teesside priority</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5" color="var(--primary-red)" /> Flat-bed transport for damaged vehicles</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="vd-muted text-xs uppercase tracking-wider mb-1 block">{label}</span>
    {children}
  </label>
);

export default RecoveryRequest;
